export const IS_MOBILE = typeof navigator !== 'undefined' &&
  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// MapTiler style (requires API key for production)
// For development, using a free OSM-based style
export const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

// PartyKit configuration
// Set VITE_PARTYKIT_HOST environment variable for production deployment
export const PARTYKIT_HOST: string | null = import.meta.env.VITE_PARTYKIT_HOST
  || (import.meta.env.DEV ? 'localhost:1999' : null);

// Validate PartyKit host is configured in production
if (!PARTYKIT_HOST && !import.meta.env.DEV) {
  console.error('VITE_PARTYKIT_HOST environment variable must be set for production builds');
}

// Flight physics constants
export interface FlightConfig {
  MIN_SPEED: number;
  MAX_SPEED: number;
  DEFAULT_SPEED: number;
  THROTTLE_RATE: number;
  PITCH_RATE: number;
  ROLL_RATE: number;
  TURN_RATE: number;
  GRAVITY: number;
  MIN_ALTITUDE: number;
  MAX_ALTITUDE: number;
  SPAWN_ALTITUDE: number;
}

export const FLIGHT: FlightConfig = {
  MIN_SPEED: 15,        // m/s - stall speed
  MAX_SPEED: 150,       // m/s
  DEFAULT_SPEED: 40,    // m/s - reduced for easier debugging
  THROTTLE_RATE: 20,    // m/s per second
  PITCH_RATE: 45,       // degrees per second
  ROLL_RATE: 60,        // degrees per second
  TURN_RATE: 0.5,       // heading change per degree of roll
  GRAVITY: 15,          // m/s descent when stalled
  MIN_ALTITUDE: 0,      // meters
  MAX_ALTITUDE: 5000,   // meters
  SPAWN_ALTITUDE: 400,  // meters - above most buildings
};

// Camera constants
export interface CameraConfig {
  DEFAULT_PITCH: number;
  MIN_PITCH: number;
  MAX_PITCH: number;
  DEFAULT_DISTANCE: number;
  ORBIT_SENSITIVITY: number;
}

export const CAMERA: CameraConfig = {
  DEFAULT_PITCH: 20,    // degrees - slight downward angle
  MIN_PITCH: 5,
  MAX_PITCH: 85,
  DEFAULT_DISTANCE: 50, // meters behind plane
  ORBIT_SENSITIVITY: 0.5,
};

// Network constants
// The client sends at 20Hz. Server-side limits retain modest headroom above
// this cadence for control messages and network jitter.
export interface NetworkConfig {
  UPDATE_RATE: number;
  INTERPOLATION_SPEED: number;
}

export const NETWORK: NetworkConfig = {
  UPDATE_RATE: 50,         // ms between position updates (20Hz)
  INTERPOLATION_SPEED: 10, // interpolation speed factor (higher = faster)
};

// Location definition
export interface Location {
  lat: number;
  lng: number;
  name: string;
}

// Starting locations with good building data
export const LOCATIONS: Record<string, Location> = {
  FLORENCE: { lat: 43.7680, lng: 11.2531, name: 'Florence' },
  BOSTON: { lat: 42.3541, lng: -71.0704, name: 'Boston' },
  NYC: { lat: 40.7580, lng: -73.9855, name: 'New York City (Midtown)' },
  SF: { lat: 37.7749, lng: -122.4194, name: 'San Francisco' },
  LONDON: { lat: 51.5074, lng: -0.1278, name: 'London' },
  TOKYO: { lat: 35.6762, lng: 139.6503, name: 'Tokyo' },
  DUBAI: { lat: 25.2048, lng: 55.2708, name: 'Dubai' },
  CHICAGO: { lat: 41.8781, lng: -87.6298, name: 'Chicago' },
};

export const DEFAULT_LOCATION: Location = LOCATIONS.FLORENCE;

// Player colors for multiplayer
export const PLAYER_COLORS: string[] = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
];

// 3D model paths - use BASE_URL for GitHub Pages compatibility
// Cessna 172 model by KOG_THORNS (https://skfb.ly/oYXVT) - CC BY 4.0
// Version parameter for cache busting (increment when model changes)
const PLANE_MODEL_VERSION = 9;
export const PLANE_MODEL_URL: string = `${import.meta.env.BASE_URL}models/cessna172-exterior.glb?v=${PLANE_MODEL_VERSION}`;

// Plane rendering settings
export interface PlaneRenderConfig {
  SCALE: number;
  MOBILE_SCALE: number;
  LOCAL_VISIBLE: boolean;
  MIN_TERRAIN_CLEARANCE: number;
}

export const PLANE_RENDER: PlaneRenderConfig = {
  SCALE: 2.4,           // Scale factor for Cessna 172 model
  MOBILE_SCALE: 1.9,    // Slightly smaller scale for mobile devices
  LOCAL_VISIBLE: true,  // show local player's plane in chase view
  MIN_TERRAIN_CLEARANCE: 50,  // Minimum altitude above terrain when teleporting (meters)
};

// Elevation/terrain settings
// Uses AWS Terrarium tiles (free, no API key required)
// Terrarium format: height = (R * 256 + G + B/256) - 32768
export interface ElevationConfig {
  TERRARIUM_URL: string;
  TERRARIUM_OFFSET: number;
  TILE_SIZE: number;
  ZOOM: number;
  TERRAIN_SEGMENTS: number;
  TERRAIN_ENABLED: boolean;
  VERTICAL_EXAGGERATION: number;
}

export const ELEVATION: ElevationConfig = {
  // AWS S3 Terrarium tiles endpoint
  TERRARIUM_URL: 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
  TERRARIUM_OFFSET: 32768.0,  // Offset for Terrarium height decoding
  TILE_SIZE: 256,             // Terrarium tiles are 256x256 pixels
  ZOOM: 12,                   // Zoom level for elevation tiles
  TERRAIN_SEGMENTS: 32,       // Subdivisions per terrain mesh (reduced for perf)
  TERRAIN_ENABLED: true,      // Enable/disable terrain elevation
  VERTICAL_EXAGGERATION: 1.0, // Multiply elevation values (1.0 = realistic)
};

// Ground texture rendering settings
// Renders vector tiles (land, water, roads) to cached textures
export interface GroundTextureConfig {
  TEXTURE_SIZE: number;           // Texture resolution (width and height in pixels)
  CACHE_MAX_SIZE: number;         // Maximum number of textures to cache
  CACHE_DISPOSE_THRESHOLD: number; // Start evicting when cache reaches this size
  TERRAIN_QUAD_SEGMENTS: number;  // Subdivisions for terrain-following quads
  ENABLED: boolean;               // Enable/disable texture-based ground rendering
  SKIP_NEIGHBOR_TILES: boolean;   // Skip fetching 8 neighbor tiles (saves network requests, MVT tiles have built-in buffer)
}

export const GROUND_TEXTURE: GroundTextureConfig = {
  TEXTURE_SIZE: IS_MOBILE ? 512 : 1024,
  CACHE_MAX_SIZE: IS_MOBILE ? 20 : 32,
  CACHE_DISPOSE_THRESHOLD: IS_MOBILE ? 15 : 24,
  TERRAIN_QUAD_SEGMENTS: IS_MOBILE ? 8 : 16,
  ENABLED: true,
  SKIP_NEIGHBOR_TILES: IS_MOBILE ? true : false,
};

// Expanded terrain settings (Z14 terrain-only outer ring)
// Loads a larger area of terrain/texture without buildings for extended visibility
// Helps avoid "edge of world" when flying
export interface ExpandedTerrainConfig {
  ENABLED: boolean;               // Enable/disable expanded terrain loading
  TILE_RADIUS: number;            // Radius for expanded terrain (4 = 9x9 grid total)
  CORE_RADIUS: number;            // Core radius with buildings (1 = 3x3 grid)
  MAX_CONCURRENT: number;         // Max expanded tiles to process at once
  UNLOAD_DISTANCE: number;        // Chebyshev distance for unloading expanded tiles
  CACHE_MAX_SIZE: number;         // Dedicated cache size for expanded tiles
  TEXTURE_SIZE: number;           // Texture resolution for expanded tiles
}

export const EXPANDED_TERRAIN: ExpandedTerrainConfig = {
  ENABLED: true,
  TILE_RADIUS: 4,
  CORE_RADIUS: 1,
  MAX_CONCURRENT: 1,
  UNLOAD_DISTANCE: 5,
  CACHE_MAX_SIZE: IS_MOBILE ? 24 : 48,
  TEXTURE_SIZE: IS_MOBILE ? 256 : 512,
};

// Web Worker settings for parallel processing
// Offloads CPU-intensive work to background threads
export interface WorkersConfig {
  GEOMETRY_ENABLED: boolean;      // Enable/disable geometry worker creation
  MVT_ENABLED: boolean;           // Enable/disable MVT parsing worker (uses zero-copy ArrayBuffer transfer)
  ELEVATION_ENABLED: boolean;     // Enable/disable elevation decoding worker
  BUILDING_GEOMETRY_ENABLED: boolean; // Enable/disable building geometry workers (extrusion in worker)
  TOTAL_BUDGET: number;           // Total workers across all pools (0 = auto based on device)
}

const workerBudgetOverride = Number.parseInt(import.meta.env.VITE_WORKER_BUDGET || '0', 10);

export const WORKERS: WorkersConfig = {
  GEOMETRY_ENABLED: true,         // Geometry workers: Always enabled (uses zero-copy ArrayBuffer transfer)
  MVT_ENABLED: true,              // MVT parsing workers: Always enabled
  ELEVATION_ENABLED: true,        // Elevation workers: Always enabled
  BUILDING_GEOMETRY_ENABLED: true, // Building geometry workers: Always enabled
  TOTAL_BUDGET: Number.isFinite(workerBudgetOverride) ? Math.max(0, workerBudgetOverride) : 0,
};

// Loading gate settings for initial tile loading
// Stalls plane movement until minimum tiles are rendered
export interface LoadingGateConfig {
  ENABLED: boolean;               // Enable/disable loading gate
  MIN_TILES: number;              // Minimum tiles before starting
  MAX_WAIT_MS: number;            // Maximum wait time (fallback timeout)
}

export const LOADING_GATE: LoadingGateConfig = {
  ENABLED: true,
  MIN_TILES: 1,                   // Wait for center tile only
  MAX_WAIT_MS: 5000,              // 5 second timeout
};

// Tile loading concurrency settings
// Limits parallel tile processing to maintain 60fps during loading
// Override with VITE_TILE_CONCURRENCY env variable
export interface TileConcurrencyConfig {
  ENABLED: boolean;               // Enable/disable concurrency limiter
  MAX_CONCURRENT: number;         // Maximum tiles processing at once
}

const defaultTileConcurrency = IS_MOBILE ? 2 : 3;
const tileConcurrencyEnv = import.meta.env.VITE_TILE_CONCURRENCY;
const parsedTileConcurrency = tileConcurrencyEnv ? parseInt(tileConcurrencyEnv, 10) : NaN;
export const TILE_CONCURRENCY: TileConcurrencyConfig = {
  ENABLED: tileConcurrencyEnv !== '0',
  MAX_CONCURRENT: Number.isNaN(parsedTileConcurrency) ? defaultTileConcurrency : parsedTileConcurrency,
};

// Network fetch concurrency settings
// Limits parallel HTTP requests to prevent flooding and improve prioritization
// Override with VITE_FETCH_CONCURRENCY env variable
export interface FetchConcurrencyConfig {
  ENABLED: boolean;               // Enable/disable fetch concurrency limiter
  MAX_CONCURRENT: number;         // Maximum concurrent network requests
}

const defaultFetchConcurrency = IS_MOBILE ? 4 : 6;
const fetchConcurrencyEnv = import.meta.env.VITE_FETCH_CONCURRENCY;
const parsedFetchConcurrency = fetchConcurrencyEnv ? parseInt(fetchConcurrencyEnv, 10) : NaN;
export const FETCH_CONCURRENCY: FetchConcurrencyConfig = {
  ENABLED: fetchConcurrencyEnv !== '0',
  MAX_CONCURRENT: Number.isNaN(parsedFetchConcurrency) ? defaultFetchConcurrency : parsedFetchConcurrency,
};

// Performance profiling settings
// Enable via VITE_PROFILING=true for Chrome DevTools visibility
export interface ProfilingConfig {
  ENABLED: boolean;               // Enable/disable profiling instrumentation
  VERBOSE: boolean;               // Log individual tile metrics to console
}

export const PROFILING: ProfilingConfig = {
  ENABLED: import.meta.env.VITE_PROFILING === 'true',
  VERBOSE: import.meta.env.VITE_PROFILING_VERBOSE === 'true',
};

// IndexedDB persistent texture caching
// Caches rendered textures for faster repeat visits
// Disabled by default in dev/PR builds, enabled in prod
// Override with VITE_TEXTURE_CACHE env variable
export interface TextureCacheConfig {
  ENABLED: boolean;
  DB_NAME: string;
  DB_SCHEMA_VERSION: number;
  STORE_NAME: string;
  MAX_ENTRIES: number;
  TTL_MS: number;
}

// Check URL parameter first (for debugging)
const urlParams = new URLSearchParams(
  typeof window === 'undefined' ? '' : window.location.search
);
const noCacheParam = urlParams.get('nocache');

// Determine if caching should be enabled:
// - URL param ?nocache=1 forces disabled (for debugging)
// - Explicitly set via VITE_TEXTURE_CACHE=true/false
// - Otherwise: disabled in dev mode, enabled in prod
const textureCacheDefault = !import.meta.env.DEV;
const textureCacheEnv = import.meta.env.VITE_TEXTURE_CACHE;
const textureCacheEnabled = noCacheParam === '1'
  ? false
  : textureCacheEnv !== undefined
    ? textureCacheEnv === 'true'
    : textureCacheDefault;

export const TEXTURE_CACHE: TextureCacheConfig = {
  ENABLED: textureCacheEnabled,
  DB_NAME: 'overture-flight-textures',
  // IndexedDB object-store schema only; rendered-pixel identity is versioned separately.
  DB_SCHEMA_VERSION: 1,
  STORE_NAME: 'textures',
  MAX_ENTRIES: IS_MOBILE ? 80 : 240,
  TTL_MS: 7 * 24 * 60 * 60 * 1000,
};
